import type { GtmEventProductImpressions } from '@packages/tracking';
import { useTracking } from '@packages/tracking';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData, graphql } from '@packages/gql/generated/shopping';

export const SearchProductCardImpressionTrackingResultFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardImpressionTrackingResultFragment on SearchProductResult {
    categories {
      id
      name
      isSelected
    }
    ... on CategoryResult {
      breadcrumbs {
        name
        pathName
      }
    }
  }
`);

export const SearchProductCardImpressionTrackingItemFragment = graphql(/* GraphQL */ `
  fragment SearchProductCardImpressionTrackingItemFragment on StylePreviewItem {
    styleName
    categoryName
    brand {
      name
    }
    listPosition
    primaryVariationGroup {
      sku
      articleNumber
      price {
        currency
        currentPrice
      }
    }
  }
`);

export const useProductImpressionTracking = (
  maskedResult: FragmentType<typeof SearchProductCardImpressionTrackingResultFragment>,
  maskedItem: FragmentType<typeof SearchProductCardImpressionTrackingItemFragment>,
) => {
  const result = getFragmentData(SearchProductCardImpressionTrackingResultFragment, maskedResult);
  const item = getFragmentData(SearchProductCardImpressionTrackingItemFragment, maskedItem);

  const dispatchGTMEvent = useTracking();

  const {
    styleName,
    categoryName,
    listPosition,
    brand: { name: brandName },
    primaryVariationGroup: {
      sku,
      articleNumber,
      price: { currency, currentPrice },
    },
  } = item;

  const filteredBreadcrumbs =
    'breadcrumbs' in result && result.breadcrumbs
      ? result.breadcrumbs.filter(({ pathName }) => pathName !== '/')
      : [];

  const breadcrumbPath = filteredBreadcrumbs.map(({ name }) => name).join('/');

  const dispatchProductImpressionTracking = () => {
    dispatchGTMEvent<GtmEventProductImpressions>({
      event: 'EECproductImpression',
      ecommerce: {
        currencyCode: currency,
        impressions: [
          {
            name: styleName,
            position: listPosition,
            brand: brandName || '',
            id: articleNumber,
            sku,
            price: currentPrice / 100,
            category: filteredBreadcrumbs.length ? breadcrumbPath : categoryName ?? 'UNKNOWN',
            list: filteredBreadcrumbs.length ? `Category: ${categoryName}` : 'Search Result',
          },
        ],
      },
    });
  };

  return { dispatchProductImpressionTracking };
};
